
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import TermsModal from './TermsModal';
import PrivacyModal from './PrivacyModal';

export default function Footer() {
  const { t } = useTranslation('common');
  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);
  const [isPrivacyModalOpen, setIsPrivacyModalOpen] = useState(false);

  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { name: 'Anasayfa', href: '/' },
    { name: 'Hakkımızda', href: '/hakkimizda' },
    { name: 'Ürünlerimiz', href: '/urunlerimiz' },
    { name: 'İletişim', href: '/iletisim' }
  ];

  const features = [
    {
      icon: 'ri-drop-line',
      title: 'Su Tasarrufu',
      description: '%40\'a kadar su tasarrufu'
    },
    {
      icon: 'ri-plant-line',
      title: 'Verim Artışı',
      description: 'Ürün kalitesinde artış'
    },
    {
      icon: 'ri-time-line',
      title: '7/24 Destek',
      description: 'Kesintisiz teknik destek'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Garanti',
      description: '2 yıl sistem garantisi'
    }
  ];

  const contactInfo = [
    {
      icon: 'ri-map-pin-line',
      text: 'Cumalı, Antalya Cd. No:9, 07500 Serik/Antalya'
    },
    {
      icon: 'ri-phone-line',
      text: '+90 553 414 52 63'
    },
    {
      icon: 'ri-mail-line',
      text: 'info@guventarsulama.com'
    }
  ];

  return (
    <>
      <footer className="bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
            {/* Company Info */}
            <div className="lg:col-span-1">
              <div className="flex items-center justify-center lg:justify-start mb-4 sm:mb-6">
                <img 
                  src="https://static.readdy.ai/image/6464ee4f71a2ee332e3d69d3173c1c98/7d3cee2b25f83d4d3d1acc2443f38ee1.png" 
                  alt="GüvenTar Sulama Sistemleri Logo" 
                  className="w-24 h-24 sm:w-32 sm:h-32 object-contain hover:scale-105 transition-transform duration-300"
                />
              </div>
              <p className="text-gray-300 mb-4 sm:mb-6 leading-relaxed text-sm sm:text-base text-center lg:text-left">
                Tarımsal sulama sistemlerinde 25 yılı aşkın tecrübe ile güvenilir çözümler sunuyoruz. 
                Su verimliliği ve sürdürülebilir tarım için yanınızdayız.
              </p>
              <div className="text-center lg:text-left">
                <h4 className="font-semibold mb-3 text-sm sm:text-base">Bizi Takip Edin</h4>
                <div className="flex justify-center lg:justify-start space-x-3 sm:space-x-4">
                  <a href="#" className="w-8 h-8 sm:w-10 sm:h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-blue-600 transition-colors cursor-pointer" title="Facebook">
                    <i className="ri-facebook-fill text-sm sm:text-lg"></i>
                  </a>
                  <a href="#" className="w-8 h-8 sm:w-10 sm:h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-blue-400 transition-colors cursor-pointer" title="Twitter">
                    <i className="ri-twitter-fill text-sm sm:text-lg"></i>
                  </a>
                  <a href="#" className="w-8 h-8 sm:w-10 sm:h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-blue-700 transition-colors cursor-pointer" title="LinkedIn">
                    <i className="ri-linkedin-fill text-sm sm:text-lg"></i>
                  </a>
                  <a href="https://www.instagram.com/guventarsulama/" target="_blank" rel="noopener noreferrer" className="w-8 h-8 sm:w-10 sm:h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-pink-600 transition-colors cursor-pointer" title="Instagram">
                    <i className="ri-instagram-fill text-sm sm:text-lg"></i>
                  </a>
                </div>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h3 className="text-lg sm:text-xl font-semibold mb-4 sm:mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                Hızlı Linkler
              </h3>
              <ul className="space-y-2 sm:space-y-3">
                {quickLinks.map((link, index) => (
                  <li key={index}>
                    <Link 
                      to={link.href}
                      className="text-gray-300 hover:text-green-400 transition-colors cursor-pointer text-sm sm:text-base"
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Features */}
            <div>
              <h3 className="text-lg sm:text-xl font-semibold mb-4 sm:mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                Avantajlarımız
              </h3>
              <div className="space-y-3 sm:space-y-4">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-green-800 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className={`${feature.icon} text-white text-sm`}></i>
                    </div>
                    <div>
                      <h4 className="text-white font-medium text-sm">{feature.title}</h4>
                      <p className="text-gray-400 text-xs">{feature.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Contact Info */}
            <div>
              <h3 className="text-lg sm:text-xl font-semibold mb-4 sm:mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                İletişim Bilgileri
              </h3>
              <ul className="space-y-3 sm:space-y-4">
                {contactInfo.map((item, index) => (
                  <li key={index} className="flex items-start space-x-2 sm:space-x-3">
                    <i className={`${item.icon} text-green-400 text-base sm:text-lg mt-0.5 flex-shrink-0`}></i>
                    <span className="text-gray-300 text-xs sm:text-sm leading-relaxed">{item.text}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-4 sm:mt-6">
                <h4 className="font-semibold mb-2 sm:mb-3 text-sm sm:text-base">Çalışma Saatleri</h4>
                <p className="text-gray-300 text-xs sm:text-sm">
                  Pazartesi - Cumartesi: 08:00 - 18:00<br />
                  Pazar: Kapalı
                </p>
              </div>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="border-t border-gray-800 mt-8 sm:mt-12 pt-6 sm:pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
              <p className="text-gray-400 text-xs sm:text-sm text-center md:text-left">
                © {currentYear} GüvenTar Sulama Sistemleri. Tüm hakları saklıdır.
              </p>
              <div className="flex flex-wrap items-center justify-center space-x-4 sm:space-x-6">
                <button 
                  onClick={() => setIsPrivacyModalOpen(true)}
                  className="text-gray-400 hover:text-green-400 text-xs sm:text-sm transition-colors cursor-pointer"
                >
                  Gizlilik Politikası
                </button>
                <button 
                  onClick={() => setIsTermsModalOpen(true)}
                  className="text-gray-400 hover:text-green-400 text-xs sm:text-sm transition-colors cursor-pointer"
                >
                  Kullanım Şartları
                </button>
                <a 
                  href="https://www.guventar.com" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-green-400 text-xs sm:text-sm transition-colors cursor-pointer"
                >
                  www.guventar.com
                </a>
              </div>
            </div>
          </div>

        </div>
      </footer>

      {/* Modals */}
      <TermsModal 
        isOpen={isTermsModalOpen} 
        onClose={() => setIsTermsModalOpen(false)} 
      />
      <PrivacyModal 
        isOpen={isPrivacyModalOpen} 
        onClose={() => setIsPrivacyModalOpen(false)} 
      />
    </>
  );
}
